import React from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LiveChat from '../components/LiveChat';
import QAChatbot from '../components/QAChatBot';
import GetQuoteButton from '../components/GetQuoteButton';
import ScrollSocialIcons from '../components/ScrollSocialIcons';
import BackToTopButton from '../components/BackToTopButton';
import '../App.css';

const TermsAndConditions = () => {
    const fadeInUp = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.6 }
    };

    return (
        <div className="legal-page-wrapper">
            <Navbar />

            {/* Hero Section */}
            <section className="legal-hero">
                <div className="legal-hero-overlay"></div>
                <motion.div
                    className="legal-hero-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="legal-hero-icon">📜</div>
                    <h1>Terms &amp; Conditions</h1>
                    <p>Please review the terms governing the use of CIAP Ltd's website and services.</p>
                    <div className="legal-last-updated">
                        Last Updated: May 25, 2026
                    </div>
                </motion.div>
            </section>

            {/* Content */}
            <section className="legal-content-section">
                <div className="legal-container">

                    <motion.div className="legal-intro" {...fadeInUp}>
                        <p>
                            Welcome to the official website of CIAP (Pvt) Ltd. By accessing and using this website, you accept
                            and agree to be bound by the following Terms and Conditions. If you do not agree to these terms,
                            please refrain from using this website or our services. These terms apply to all visitors, users,
                            clients, and any other parties who access or use our website and services.
                        </p>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">01</div>
                        <h2>Acceptance of Terms</h2>
                        <div className="legal-card">
                            <p>
                                By accessing this website, you acknowledge that you have read, understood, and agree to be bound by these
                                Terms and Conditions, along with our Privacy Policy. CIAP reserves the right to modify these terms at any
                                time without prior notice. Continued use of the website following any changes constitutes your acceptance
                                of the revised terms.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">02</div>
                        <h2>Services Overview</h2>
                        <p>CIAP (Pvt) Ltd provides the following services through its website and operations:</p>
                        <div className="legal-grid">
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">🚢</div>
                                <h4>Marine Engineering</h4>
                                <p>Comprehensive marine repair, maintenance, and engineering solutions for vessels of all sizes.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">🔧</div>
                                <h4>Ship Repairs & Docking</h4>
                                <p>Dry docking services, hull treatment, and scheduled maintenance programs.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">🏗️</div>
                                <h4>Shipbuilding</h4>
                                <p>Design and construction of marine vessels to international classification standards.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">⚙️</div>
                                <h4>Industrial Engineering</h4>
                                <p>Mechanical and automation solutions for industrial facilities and power plants.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">🐟</div>
                                <h4>Ship Husbandry</h4>
                                <p>Underwater inspections, hull cleaning, propeller polishing, and related services.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">📦</div>
                                <h4>Spare Parts Supply</h4>
                                <p>Procurement and supply of genuine marine spare parts and equipment.</p>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">03</div>
                        <h2>Use of Website</h2>
                        <div className="legal-card">
                            <h3>Permitted Use</h3>
                            <ul>
                                <li>Access and browse the website for informational purposes</li>
                                <li>Submit inquiries, quotation requests, and service-related forms</li>
                                <li>Apply for career opportunities listed on our careers portal</li>
                                <li>Register as a supplier or business partner through our designated forms</li>
                            </ul>
                        </div>
                        <div className="legal-card">
                            <h3>Prohibited Activities</h3>
                            <ul>
                                <li>Unauthorized access to restricted areas of the website or server infrastructure</li>
                                <li>Reproduction, distribution, or modification of website content without written consent</li>
                                <li>Uploading malicious software, viruses, or harmful code</li>
                                <li>Engaging in any activity that disrupts or interferes with website functionality</li>
                                <li>Using automated systems (bots, scrapers) to collect data from the website</li>
                                <li>Impersonation of any person or entity, or misrepresenting your affiliation</li>
                            </ul>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">04</div>
                        <h2>Intellectual Property</h2>
                        <div className="legal-card">
                            <p>
                                All content on this website — including but not limited to text, graphics, logos, images, videos,
                                branding, design elements, and proprietary software — is the exclusive property of CIAP (Pvt) Ltd
                                or its licensors. This content is protected by applicable copyright, trademark, and intellectual
                                property laws.
                            </p>
                            <p>
                                You may not copy, reproduce, distribute, transmit, display, or create derivative works from any
                                content on this website without our express written permission. The CIAP name, logo, and all
                                related names, logos, product and service names, designs, and slogans are trademarks of CIAP (Pvt) Ltd.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">05</div>
                        <h2>Service Agreements</h2>
                        <div className="legal-card">
                            <h3>Quotations & Contracts</h3>
                            <p>
                                Any quotations provided through this website or our communication channels are estimates and
                                subject to formal written agreements. Final pricing, terms, and conditions of specific marine
                                or industrial services are governed by individual contracts entered into between CIAP and the client.
                            </p>
                        </div>
                        <div className="legal-card">
                            <h3>Service Standards</h3>
                            <p>
                                CIAP strives to deliver services in accordance with applicable international maritime standards,
                                classification society requirements, and relevant industry regulations. However, specific warranties
                                and guarantees are subject to the terms of individual service contracts.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">06</div>
                        <h2>Limitation of Liability</h2>
                        <div className="legal-card legal-card-highlight">
                            <p>
                                <strong>To the fullest extent permitted by law</strong>, CIAP (Pvt) Ltd and its directors, employees,
                                agents, and affiliates shall not be liable for any indirect, incidental, special, consequential, or
                                punitive damages arising from:
                            </p>
                            <ul>
                                <li>Your use of or inability to use this website</li>
                                <li>Any errors, inaccuracies, or omissions in website content</li>
                                <li>Unauthorized access to or alteration of your transmissions or data</li>
                                <li>Any third-party content or conduct on the website</li>
                                <li>Business interruption or loss of profits related to website usage</li>
                            </ul>
                            <p>
                                This website is provided on an "as is" and "as available" basis without warranties of any kind,
                                either express or implied.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">07</div>
                        <h2>Third-Party Links</h2>
                        <div className="legal-card">
                            <p>
                                Our website may contain links to external websites or third-party services that are not operated
                                or controlled by CIAP. We are not responsible for the content, privacy policies, or practices of
                                any third-party websites. Accessing third-party links is at your own risk, and we encourage you
                                to review the terms and privacy policies of any external sites you visit.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">08</div>
                        <h2>Indemnification</h2>
                        <div className="legal-card">
                            <p>
                                You agree to indemnify, defend, and hold harmless CIAP (Pvt) Ltd, its officers, directors, employees,
                                agents, and affiliates from any and all claims, liabilities, damages, costs, and expenses (including
                                reasonable legal fees) arising out of or related to your use of this website, violation of these
                                Terms and Conditions, or infringement of any intellectual property or other rights of any person or entity.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">09</div>
                        <h2>Governing Law</h2>
                        <div className="legal-card">
                            <p>
                                These Terms and Conditions shall be governed by and construed in accordance with the laws of the
                                Democratic Socialist Republic of Sri Lanka. Any disputes arising under or in connection with these
                                terms shall be subject to the exclusive jurisdiction of the courts of Sri Lanka.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">10</div>
                        <h2>Modifications</h2>
                        <div className="legal-card">
                            <p>
                                CIAP reserves the right to update, change, or replace any part of these Terms and Conditions at any time
                                by posting updates to this page. It is your responsibility to check this page periodically for changes.
                                Your continued use of the website following the posting of any changes constitutes acceptance of those changes.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">11</div>
                        <h2>Contact Us</h2>
                        <div className="legal-contact-card">
                            <p>For questions about these Terms and Conditions, please contact us:</p>
                            <div className="legal-contact-details">
                                <div className="legal-contact-item">
                                    <span className="legal-contact-label">Email</span>
                                    <a href="mailto:info@ciap.lk">info@ciap.lk</a>
                                </div>
                                <div className="legal-contact-item">
                                    <span className="legal-contact-label">Address</span>
                                    <span>CIAP (Pvt) Ltd, Sri Lanka</span>
                                </div>
                                <div className="legal-contact-item">
                                    <span className="legal-contact-label">Website</span>
                                    <a href="https://www.ciap.lk">www.ciap.lk</a>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                </div>
            </section>

            <LiveChat />
            <QAChatbot />
            <GetQuoteButton />
            <ScrollSocialIcons />
            <BackToTopButton />
            <Footer />
        </div>
    );
};

export default TermsAndConditions;
